import { fr } from "@codegouvfr/react-dsfr";
import { Table } from "@codegouvfr/react-dsfr/Table";
import { tss } from "tss-react/dsfr";
import { EVALUATION_TEXTS } from "../../utils/texts/evaluation";
import {
  getReadableCardinality,
  getReadableSource,
  replacePlaceholders,
} from "../../utils/tools";
import type { DiagnosticItem, LandIntersection } from "../../utils/types";

type DiagnosticEvaluationProps = {
  diagnosticItem: DiagnosticItem;
};

const DiagnosticEvaluation = ({
  diagnosticItem,
}: DiagnosticEvaluationProps) => {
  const { cx, classes } = useStyles();

  const {
    diagnostic: {
      max_db_lden,
      min_db_lden,
      land_intersections_ld,
      land_intersections_ln,
      air_intersections,
      flags,
    },
  } = diagnosticItem;

  const getDbLevelsSpecification = () => {
    const dbBuffer = 10;
    return (
      " " +
      (max_db_lden > min_db_lden + dbBuffer
        ? EVALUATION_TEXTS.INFORMATIONS.NOISE_LEVELS.LEVEL_VARIATION_HIGH
        : EVALUATION_TEXTS.INFORMATIONS.NOISE_LEVELS.LEVEL_VARIATION_LOW)
    );
  };

  const getMultiExposedLdenLnSpecification = () => {
    const dbBuffer = 10;

    const ldenDb = Math.max(
      ...land_intersections_ld.map((item) => item.acoustic_db_value)
    );
    const lnDb =
      Math.max(...land_intersections_ln.map((item) => item.acoustic_db_value)) + dbBuffer;

    if (ldenDb === lnDb) {
      return ".";
    }

    return (
      " " +
      (ldenDb > lnDb
        ? EVALUATION_TEXTS.INFORMATIONS.EXPOSURE.DAY_AND_NIGHT.NIGHT_LOWER
        : EVALUATION_TEXTS.INFORMATIONS.EXPOSURE.DAY_AND_NIGHT.DAY_LOWER)
    );
  };

  const getLnCodeInfraLegende = (codeinfra: string | null) => {
    if (!codeinfra) return "-";

    const ln_intersection = land_intersections_ln.find(
      (intersection) => intersection.codeinfra === codeinfra
    );

    return ln_intersection ? ln_intersection.acoustic_db_value + " dB" : "-";
  };

  const getPercentImpactedFromCodeInfra = (
    landIntersections: LandIntersection[],
    codeinfra: string | null
  ) => {
    const computedPercentImpacted = landIntersections
      .filter(
        (intersection) =>
          intersection.codeinfra === codeinfra && intersection.acoustic_noisemap_kind === "A"
      )
      .reduce((acc, current) => acc + current.percent_impacted, 0);

    return computedPercentImpacted === 0.99
      ? 1
      : computedPercentImpacted > 1
        ? 1
        : computedPercentImpacted;
  };

  const getUniqueIntersectionsByCodeInfra = () => {
    const maxLegendeByInfra: { [key: string]: LandIntersection } = {};

    land_intersections_ld.forEach((entry) => {
      if (entry.acoustic_noisemap_kind === "A") {
        const key = entry.codeinfra || `null_${entry.kind}`;
        if (
          !maxLegendeByInfra[key] ||
          entry.acoustic_db_value > maxLegendeByInfra[key].acoustic_db_value
        ) {
          maxLegendeByInfra[key] = entry;
        }
      }
    });

    return Object.values(maxLegendeByInfra).sort(
      (a, b) => b.acoustic_db_value - a.acoustic_db_value
    );
  };

  const land_intersections_ld_unique_display =
    getUniqueIntersectionsByCodeInfra();

  return (
    <div className={cx(classes.container)}>
      <h4 className={fr.cx("fr-text--lg", "fr-mb-4v")}>Cartes de bruit</h4>
      <div className={cx(classes.section)}>
        <p className={cx(fr.cx("fr-mb-0"))}>
          {EVALUATION_TEXTS.INFORMATIONS.INTRODUCTION}
        </p>
        <ul>
          <li
            dangerouslySetInnerHTML={{
              __html:
                flags.isMultiExposedSources || flags.isMultiExposedLandSources
                  ? replacePlaceholders(
                    EVALUATION_TEXTS.INFORMATIONS.CHARACTERISTICS
                      .MULTI_EXPOSURE,
                    {
                      sources: Array.from(
                        new Set(
                          land_intersections_ld.map(
                            (intersection) =>
                              `<b>${getReadableSource(
                                intersection.kind
                              )}</b>`
                          )
                        )
                      ).join(" et "),
                    }
                  )
                  : EVALUATION_TEXTS.INFORMATIONS.CHARACTERISTICS
                    .NO_MULTI_EXPOSURE,
            }}
          />
          {!!land_intersections_ld[0] && (
            <li
              dangerouslySetInnerHTML={{
                __html:
                  land_intersections_ld[0].acoustic_producer_kind === "INFRA"
                    ? replacePlaceholders(
                      EVALUATION_TEXTS.INFORMATIONS.CHARACTERISTICS
                        .MAIN_SOURCE_INFRA,
                      {
                        typesource: getReadableSource(
                          land_intersections_ld[0].kind
                        ),
                        codinfra:
                          land_intersections_ld[0].codeinfra || "nom inconnu",
                        percent_impacted: Math.round(
                          getPercentImpactedFromCodeInfra(
                            land_intersections_ld,
                            land_intersections_ld[0].codeinfra
                          ) * 100
                        ),
                        direction: getReadableCardinality(
                          land_intersections_ld[0].direction
                        ),
                      }
                    )
                    : EVALUATION_TEXTS.INFORMATIONS.CHARACTERISTICS
                      .MAIN_SOURCE_AGGLO,
              }}
            />
          )}
          {flags.isMultiExposedLdenLn && (
            <li
              dangerouslySetInnerHTML={{
                __html:
                  EVALUATION_TEXTS.INFORMATIONS.EXPOSURE.DAY_AND_NIGHT.INFO +
                  getMultiExposedLdenLnSpecification(),
              }}
            />
          )}
          <li
            dangerouslySetInnerHTML={{
              __html:
                replacePlaceholders(
                  EVALUATION_TEXTS.INFORMATIONS.NOISE_LEVELS.LEVEL_INFO,
                  {
                    levelMax: max_db_lden,
                    levelMin: min_db_lden,
                  }
                ) + getDbLevelsSpecification(),
            }}
          />
          <li
            dangerouslySetInnerHTML={{
              __html: flags.isPriorityZone
                ? EVALUATION_TEXTS.INFORMATIONS.PRIORITY_ZONE.IN_PRIORITY_ZONE
                : EVALUATION_TEXTS.INFORMATIONS.PRIORITY_ZONE
                  .NOT_IN_PRIORITY_ZONE,
            }}
          />
        </ul>
      </div>
      <div className={cx(classes.sourcesTable)}>
        {(!!land_intersections_ld_unique_display.length ||
          !!air_intersections.length) && (
            <Table
              caption="Tableau de synthèse des niveaux de bruit par source"
              noCaption
              bordered
              data={land_intersections_ld_unique_display
                .filter((intersection) => intersection.acoustic_noisemap_kind === "A")
                .map((intersection) => [
                  getReadableSource(intersection.kind, true),
                  intersection.acoustic_producer_kind === "INFRA" ? "État" : "Agglomération",
                  intersection.codeinfra || "Non connu",
                  intersection.acoustic_db_value + " dB",
                  getLnCodeInfraLegende(intersection.codeinfra),
                ])
                .concat(
                  air_intersections.map((intersection) => [
                    "Aérien",
                    "État",
                    intersection.label || "Non connu",
                    intersection.acoustic_db_value + " dB",
                    "-",
                  ])
                )}
              headers={[
                "Type de source",
                "Producteur",
                "Nom de la source",
                "Niveau de bruit (jour)",
                "Niveau de bruit (nuit)",
              ]}
            />
          )}
      </div>
    </div>
  );
};

const useStyles = tss.create(() => ({
  container: {
    ul: {
      marginTop: fr.spacing("4v"),
      marginLeft: fr.spacing("4v"),
      li: {
        lineHeight: "1.75rem",
        "&:not(:last-child)": {
          marginBottom: fr.spacing("2v"),
        },
      },
    },
    paddingTop: fr.spacing("11v"),
    [fr.breakpoints.down("md")]: {
      paddingTop: fr.spacing("2v"),
    },
  },
  section: {
    padding: `${fr.spacing("2v")} ${fr.spacing("2v")} ${fr.spacing(
      "2v"
    )} ${fr.spacing("10v")}`,
    marginLeft: fr.spacing("6v"),
    borderLeft: `4px solid ${fr.colors.decisions.border.default.blueFrance.default}`,
    ul: {
      marginBottom: 0,
    },
  },
  sourcesTable: {
    marginTop: fr.spacing("8v"),
    display: "flex",
    justifyContent: "center",
  },
}));

export default DiagnosticEvaluation;
